import React, { Suspense, lazy, useEffect, useState } from 'react';
import {
  Routes,
  Route,
  Navigate,
  Outlet,
  useNavigate,
  useLocation,
  useParams,
} from 'react-router-dom';
import i18n from 'i18next';
import './App.css';

// Pages
import Login from './pages/Login';
import Landing from './pages/Landing';
import { Dashboard } from './pages/Dashboard/Dashboard';
import { Questions } from './pages/Questions/Questions';
import { Cover } from './pages/Cover/Cover';
import { Chapter } from './pages/Chapter/Chapter';
import { Photos } from './pages/Photos/Photos';
import { Influencer } from './pages/Influencer/Influencer';
import Terms from './pages/Terms';
import Checkout from './pages/Checkout/Checkout';
import CheckoutSuccess from './pages/Checkout/CheckoutSuccess';
import CheckoutCancel from './pages/Checkout/CheckoutCancel';

// Guards
import { BookStatusGuard } from './components/BookStatusGuard/BookStatusGuard';

// i18n helpers
import {
  SUPPORTED_CODES,
  FALLBACK_LANGUAGE,
  STORAGE_KEY,
} from './i18n/languages';
import { detectLanguageByIp } from './i18n/detectByIp';

// Ролик кабинета — отдельным чанком, в основной бандл не попадает
const CabinetPreview = lazy(() => import('./preview/CabinetPreviewPage'));

function AuthGuard({ children }: { children: React.ReactNode }) {
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token && location.pathname.includes('/forms')) {
      navigate('/login', { replace: true });
    }
  }, [location.pathname, navigate]);

  return <>{children}</>;
}

/**
 * Lang scope used ONLY for the landing route `/<lang>`.
 * Validates :lang against SUPPORTED_CODES and keeps i18n + localStorage in sync.
 * Unknown first segments are treated as paths and redirected to the bare path
 * (so old shareable links like /ru/login → /login + storage=ru still work).
 */
function LangScope() {
  const { lang } = useParams<{ lang: string }>();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (!lang) return;
    if (!SUPPORTED_CODES.includes(lang)) {
      // Unknown lang → the segment was actually a path (e.g. /login).
      // Bounce to the bare path; the non-landing routes pick lang from storage.
      navigate(location.pathname.replace(/^\/[^/]+/, '') || '/' + (location.search || '') + (location.hash || ''), { replace: true });
      return;
    }
    if (i18n.language !== lang) {
      i18n.changeLanguage(lang);
    }
    try {
      localStorage.setItem(STORAGE_KEY, lang);
    } catch {
      /* localStorage unavailable */
    }
  }, [lang, navigate, location.pathname, location.search, location.hash]);

  return <Outlet />;
}

/**
 * Wrapper for non-landing routes — pulls the language from localStorage
 * (or fallback) and syncs i18n. The URL stays clean (no `/<lang>` prefix).
 */
function LangFromStorage() {
  useEffect(() => {
    let lang = FALLBACK_LANGUAGE;
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored && SUPPORTED_CODES.includes(stored)) lang = stored;
    } catch {
      /* localStorage unavailable */
    }
    if (i18n.language !== lang) {
      i18n.changeLanguage(lang);
    }
  }, []);

  return <Outlet />;
}

/**
 * Catches `/:lang/<rest>` for backward-compat with old per-lang links.
 * Saves the lang to storage and strips the prefix.
 */
function LangPathRedirect() {
  const { lang } = useParams<{ lang: string }>();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (lang && SUPPORTED_CODES.includes(lang)) {
      try {
        localStorage.setItem(STORAGE_KEY, lang);
      } catch {
        /* ignore */
      }
      i18n.changeLanguage(lang);
    }
    const stripped = location.pathname.replace(/^\/[^/]+/, '') || '/';
    navigate(stripped + (location.search || '') + (location.hash || ''), { replace: true });
  }, [lang, location.pathname, location.search, location.hash, navigate]);

  return null;
}

/**
 * Root '/' handler: pick a language (URL > localStorage > IP > fallback)
 * and redirect to '/<lang>/'.
 */
function RootRedirect() {
  const navigate = useNavigate();
  const [decided, setDecided] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      let lang = '';
      try {
        const stored = localStorage.getItem(STORAGE_KEY);
        if (stored && SUPPORTED_CODES.includes(stored)) lang = stored;
      } catch {
        /* ignore */
      }
      if (!lang) {
        lang = await detectLanguageByIp();
      }
      if (cancelled) return;
      navigate('/' + lang, { replace: true });
      setDecided(true);
    })();
    return () => {
      cancelled = true;
    };
  }, [navigate]);

  // Render nothing while we decide. Avoids a flash of fallback UI.
  return decided ? null : null;
}

function App() {
  return (
    <AuthGuard>
      <Routes>
        {/* Root: detect language and redirect to /<lang> (landing). */}
        <Route path="/" element={<RootRedirect />} />

        {/* Landing — the only page that keeps /<lang> in the URL. */}
        <Route path="/:lang" element={<LangScope />}>
          <Route index element={<Landing />} />
        </Route>

        {/* Backward-compat: old /<lang>/<rest> links → save lang, strip prefix. */}
        <Route path="/:lang/*" element={<LangPathRedirect />} />

        {/* All other pages: lang read from localStorage, no URL prefix. */}
        <Route element={<LangFromStorage />}>
          {/* Public */}
          <Route path="/login" element={<Login />} />
          <Route path="/terms" element={<Terms />} />
          <Route
            path="/preview/cabinet"
            element={<Suspense fallback={null}><CabinetPreview /></Suspense>}
          />
          <Route path="/checkout" element={<Checkout />} />
          <Route path="/checkout/success" element={<CheckoutSuccess />} />
          <Route path="/checkout/cancel" element={<CheckoutCancel />} />

          {/* Protected */}
          <Route path="/forms" element={<Dashboard />} />
          <Route path="/influencer" element={<Influencer />} />
          <Route
            path="/forms/questions/:id"
            element={<BookStatusGuard><Questions /></BookStatusGuard>}
          />
          <Route
            path="/forms/cover/:id"
            element={<BookStatusGuard><Cover /></BookStatusGuard>}
          />
          <Route
            path="/forms/photos/:templateId/:photoId"
            element={<BookStatusGuard><Photos /></BookStatusGuard>}
          />
          <Route
            path="/forms/chapter/:templateId/:chapterId"
            element={<BookStatusGuard><Chapter /></BookStatusGuard>}
          />
        </Route>

        {/* Fallback */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </AuthGuard>
  );
}

export default App;
